import { useState } from 'react';
import { FORMAT_DEFINITIONS, CANS_PER_KEG } from './constants';
import { calculateFormatOutput, getQuantityValueForFormat } from './scenarioCalculations';
import { formatCurrency, formatQuantityWithUnit } from './formatters';
import { BottleIcon, CanIcon, JugIcon, KegIcon, WarningIcon } from './icons';

const getFormatIcon = (formatId) => {
    if (formatId === 'cans_rtd') return CanIcon;
    if (formatId === 'keg_5gal_rtd') return KegIcon;
    if (formatId === 'gallon_conc') return JugIcon;
    return BottleIcon;
};

const WizardStep3Quantity = ({
    activeProduct,
    availableKegs,
    selectedFormatId,
    formatAllocations,
    targetAllocations,
    finishedKegs,
    scenarioInventory,
    showStandardized,
    targetBottleneck,
    viewMode,
    onFormatAllocation,
    onTargetAllocation,
    onFinishedKegsChange,
    onInventoryChange,
    onReviewCreate,
}) => {
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const format = FORMAT_DEFINITIONS[selectedFormatId];

    if (!format) {
        return (
            <div className="gsp-wizard-step gsp-step3 gsp-animate-in">
                <div className="gsp-step-section-header">
                    <span className="gsp-step-num-badge">3</span>
                    <div>
                        <h2 className="gsp-step-title">Set quantity for the selected format</h2>
                        <p className="gsp-step-subtitle">Select a format in Step 2 to continue.</p>
                    </div>
                </div>
            </div>
        );
    }

    const Icon = getFormatIcon(selectedFormatId);

    const currentValue = getQuantityValueForFormat(
        viewMode,
        formatAllocations,
        targetAllocations,
        activeProduct.product_id,
        selectedFormatId,
        availableKegs
    );

    const handleChange = (val) => {
        if (viewMode === 'target') {
            onTargetAllocation(activeProduct.product_id, selectedFormatId, val);
        } else {
            onFormatAllocation(activeProduct.product_id, selectedFormatId, val);
        }
    };

    // Live summary calculations
    let projectedUnits = 0;
    let wsRevenue = 0;
    let retailRevenue = 0;
    let inventoryUsedKegs = 0;

    if (currentValue > 0) {
        if (viewMode === 'capacity') {
            const result = calculateFormatOutput(currentValue, selectedFormatId);
            projectedUnits = result.units;
            wsRevenue = result.wsRevenue;
            retailRevenue = result.retailRevenue;
            inventoryUsedKegs = currentValue;
        } else {
            projectedUnits = currentValue;
            wsRevenue = currentValue * format.pricing.ws;
            retailRevenue = currentValue * format.pricing.retail;
            inventoryUsedKegs = currentValue / format.per_concentrate_keg;
        }
    }

    const limitingIngredientIds = new Set(
        (targetBottleneck?.limitingIngredients || []).map(ing => ing.ingredient_id)
    );

    const quantityLabel = viewMode === 'target' ? 'Target quantity' : 'Allocate concentrate';
    const quantityUnit = viewMode === 'target' ? format.unit : 'kegs';
    const inputDisplayValue = currentValue > 0 ? Number(currentValue.toFixed(2)) : '';

    // Target mode only: capacity mode already caps allocation at input time,
    // so it can never go over. Target mode intentionally allows it, so the
    // system needs to explain why and how to fix it.
    const isOverCapacity = viewMode === 'target' && currentValue > 0 && targetBottleneck && !targetBottleneck.isFeasible;
    const topLimiting = targetBottleneck?.limitingIngredients?.[0];
    const shortfallKegs = topLimiting?.shortfallKegs || 0;
    const maxFeasibleUnits = calculateFormatOutput(availableKegs, selectedFormatId).units;
    const maxFeasibleValue = Math.floor(maxFeasibleUnits * 100) / 100;

    return (
        <div className="gsp-wizard-step gsp-step3 gsp-animate-in">
            <div className="gsp-step-section-header">
                <span className="gsp-step-num-badge">3</span>
                <div>
                    <h2 className="gsp-step-title">Set quantity for the selected format</h2>
                    <p className="gsp-step-subtitle">
                        Only the chosen format expands. Everything else stays collapsed.
                    </p>
                </div>
            </div>

            {/* Quantity Editor Card */}
            <div className="gsp-quantity-card">
                {/* Format + Input Row */}
                <div className="gsp-quantity-row">
                    <div className="gsp-quantity-format-info">
                        <div className="gsp-quantity-format-icon">
                            <Icon />
                        </div>
                        <div className="gsp-quantity-format-text">
                            <strong>{format.name}</strong>
                            <span>{format.per_concentrate_keg} {format.unit} per concentrate keg</span>
                        </div>
                    </div>

                    <div className="gsp-quantity-input-group">
                        <label className="gsp-quantity-label">{quantityLabel}</label>
                        <div className="gsp-quantity-input-row">
                            <input
                                type="number"
                                className="gsp-quantity-input"
                                value={inputDisplayValue}
                                onChange={e => handleChange(e.target.value)}
                                placeholder="0.0"
                                min="0"
                                step="0.5"
                            />
                            <span className="gsp-quantity-unit">{quantityUnit}</span>
                        </div>
                    </div>

                    <div className="gsp-keep-selected-badge">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Keep selected
                    </div>
                </div>

                {/* Live Summary Strip */}
                <div className="gsp-live-summary">
                    <div className="gsp-live-summary-item">
                        <span>PROJECTED OUTPUT</span>
                        <strong>
                            {currentValue > 0
                                ? `${Math.floor(projectedUnits).toLocaleString()} ${format.unit}`
                                : '—'}
                        </strong>
                    </div>
                    <div className="gsp-live-summary-divider" />
                    <div className="gsp-live-summary-item">
                        <span>INVENTORY USED</span>
                        <strong>
                            {currentValue > 0
                                ? `${inventoryUsedKegs.toFixed(2)} kegs`
                                : '—'}
                        </strong>
                    </div>
                    <div className="gsp-live-summary-divider" />
                    <div className="gsp-live-summary-item">
                        <span>EST. REVENUE (WS / RETAIL)</span>
                        <strong>
                            {wsRevenue > 0
                                ? `${formatCurrency(wsRevenue)} / ${formatCurrency(retailRevenue)}`
                                : '—'}
                        </strong>
                    </div>
                </div>

                {/* Bottleneck banner — always visible, not hidden behind Advanced */}
                {isOverCapacity && (
                    <div className="gsp-bottleneck-banner gsp-animate-in">
                        <WarningIcon />
                        <div className="gsp-bottleneck-banner-text">
                            <strong>Target exceeds available concentrate</strong>
                            <p>
                                Limited by <strong>{topLimiting.ingredient_name}</strong> — you need{' '}
                                {(availableKegs + shortfallKegs).toFixed(2)} kegs of concentrate but only{' '}
                                {availableKegs.toFixed(2)} are available (short by {shortfallKegs.toFixed(2)} kegs).
                                The max target you can set is about{' '}
                                {Math.floor(maxFeasibleUnits).toLocaleString()} {format.unit}.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="gsp-bottleneck-fix-btn"
                            onClick={() => handleChange(String(maxFeasibleValue))}
                        >
                            Use max feasible ({Math.floor(maxFeasibleUnits).toLocaleString()} {format.unit})
                        </button>
                    </div>
                )}

                {/* Advanced Capacity Section */}
                <div className="gsp-advanced-section">
                    <button
                        className={`gsp-advanced-toggle ${advancedOpen ? 'open' : ''}`}
                        onClick={() => setAdvancedOpen(prev => !prev)}
                        aria-expanded={advancedOpen}
                    >
                        <span className="gsp-advanced-arrow">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" style={{ transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </span>
                        <span>Advanced capacity assumptions</span>
                        <span className="gsp-advanced-chevron-right">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" style={{ transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                <polyline points="18 15 12 9 6 15" />
                            </svg>
                        </span>
                    </button>

                    {advancedOpen && (
                        <div className="gsp-advanced-body gsp-animate-in">
                            <div className="gsp-advanced-row">
                                <span>From ingredients</span>
                                <strong>
                                    {(activeProduct.production_capacity_cans / CANS_PER_KEG).toFixed(2)} kegs
                                </strong>
                            </div>

                            <div className="gsp-advanced-row">
                                <span>Finished kegs on hand</span>
                                <input
                                    type="number"
                                    className="gsp-advanced-input"
                                    value={finishedKegs[activeProduct.product_id] ?? ''}
                                    onChange={e => onFinishedKegsChange(activeProduct.product_id, e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    step="0.5"
                                />
                            </div>

                            {activeProduct.bottleneck_ingredient && !targetBottleneck && (
                                <div className="gsp-bottleneck-alert">
                                    <WarningIcon />
                                    <span>
                                        Limited by <strong>{activeProduct.bottleneck_ingredient.name}</strong>
                                    </span>
                                </div>
                            )}

                            <div className="gsp-ingredients-grid">
                                {activeProduct.ingredient_analysis.map(ing => (
                                    <div
                                        key={ing.ingredient_id}
                                        className={`gsp-ingredient-card ${limitingIngredientIds.has(ing.ingredient_id) ? 'bottleneck' : ''}`}
                                    >
                                        <strong>{ing.ingredient_name}</strong>
                                        <span className="gsp-ingredient-amount">
                                            {formatQuantityWithUnit(
                                                ing.required_per_keg,
                                                ing.ingredient_unit,
                                                ing.ingredient_category,
                                                showStandardized,
                                                ing.standardized_required,
                                                ing.base_unit
                                            )}
                                        </span>
                                        <input
                                            type="number"
                                            className="gsp-advanced-input"
                                            value={scenarioInventory[ing.ingredient_id] || ing.available_quantity}
                                            onChange={e => onInventoryChange(ing.ingredient_id, e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Review & Create CTA */}
            {currentValue > 0 && (
                <div className="gsp-step-cta gsp-animate-in">
                    <button
                        className="gsp-review-btn"
                        onClick={onReviewCreate}
                        disabled={isOverCapacity}
                        title={isOverCapacity ? 'Fix the concentrate shortfall above before continuing' : undefined}
                    >
                        {isOverCapacity ? 'Fix the shortfall above to continue' : 'Review & Create Production Plan'}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default WizardStep3Quantity;
